import React from 'react';

import _ from 'lodash';
import { useSelector } from 'react-redux';

import { WidgetsContext } from './index.jsx';

import { FORM, FORM_STEP, FORM_FIELD, PAGE, EXTERNAL_FORM, LIVE_ELEMENT_CONTAINER } from '../../../constants/widgetTypes';

export function useWidgetSelector(id) {
  const context = React.useContext(WidgetsContext);
  const selector = context.selector;

  var widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);
  return widgets.find(item => {
    return item.uid === id;
  });
}

export function useWidgetChildrenSelector(parent) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  return widgets.filter(item => {
    return item.parent === parent;
  }).filter(item => _.get(item, `properties.${context.breakpoint}.visible`, true))
    .sort((w1, w2) => {
      const o1 = _.get(w1, 'order') || 1;
      const o2 = _.get(w2, 'order') || 1;

      return o1 - o2;
    });
}

export function useWidgetSelectorForType(type) {
  const context = React.useContext(WidgetsContext);
  const selector = context.selector;

  var widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);
  return widgets.filter(item => {
    return item.type === type;
  });
}

/**
 * Return Closest parent for type
*/
export function useParentWidgetSelectorForType(widget, widgetType) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const _findParent = (widgets, widget) => {
    const item = findWidget(widgets, widget);

    if (!item) {
      return null;
    }

    const type = _.get(item, 'type');
    if (type === widgetType) {
      return item;
    }

    if (item && item.parent) {
      return _findParent(widgets, item.parent)
    }
  }

  return _findParent(widgets, widget);
}

/**
 * Return closest container for widget
*/
export function useContainerSelector(widget) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);
  const item = widgets.find(item => item.uid === widget);

  if (item && item.parent) {
    return widgets.find(element => element.uid === item.parent);
  }

  return null;
}


/**
 * Return Form for Form field
*/
export function useForm(parent) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const form = widgets.find(item => {
    return item.type === FORM && item.uid === parent
  });

  return form;
}


/**
 * Return Input Widgets for Form
*/
export function useFormInputs(form) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  var formSteps = widgets.filter(item => item.type === FORM_STEP && item.parent === form);

  formSteps = _.orderBy(formSteps, ['order'], ['asc']);
  const formStep = formSteps[0];

  const inputWidgets = widgets.filter(item => {
    return item.type === FORM_FIELD && item.parent === formStep.uid
  });

  return inputWidgets;
}

/**
 * Return closest page widget for widget
*/
export function usePageWidgetSelector(widget) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const _findParent = (widgets, widget) => {
    const item = findWidget(widgets, widget);

    if (!item) {
      return null;
    }

    const type = _.get(item, 'type');
    if (type === PAGE) {
      return item;
    }

    if (item && item.parent) {
      return _findParent(widgets, item.parent)
    }
  }

  return _findParent(widgets, widget);
}

function findWidget(widgets, widget) {
  return widgets.find(item => item.uid === widget);
}

/**
 * Return if widgets parent is liveElemenet
*/
export function useLiveElementSelector(widget) {
  const context = React.useContext(WidgetsContext);

  const selector = context.selector;
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const _findParent = (widgets, widget) => {
    const item = findWidget(widgets, widget);

    if (!item) {
      return false;
    }

    const type = _.get(item, 'type');
    if (type === LIVE_ELEMENT_CONTAINER) {
      return true;
    }

    if (item && item.parent) {
      return _findParent(widgets, item.parent)
    }
  }

  return _findParent(widgets, widget);
}

/**
 * Return if any form has success state
*/
export function useFormSuccessSelector(selector) {
  const widgets = useSelector(state => _.get(state, `${selector}.widgets`) || []);

  const formWidgets = widgets.filter(item => item.type === FORM || item.type === EXTERNAL_FORM);

  var formSuccess = formWidgets.some(item => item.success);
  return formSuccess;
}

/**
 * Return sum of the totol time spent in page
*/
export function usePageDuration() {
  var pages = useSelector(state => _.get(state, 'pages.analytics.pages', []))

  let duration = _.reduce(pages, (sum, page) => sum + page.duration, 0);

  return duration;
}


/**
 * Check if the element is visible
*/
export function useOnScreen(ref) {
  const [isOnScreen, setIsOnScreen] = React.useState(false);
  const observerRef = React.useRef(null);

  React.useEffect(() => {
    observerRef.current = new IntersectionObserver(([entry]) =>
      setIsOnScreen(entry.isIntersecting)
    );
  }, []);

  React.useEffect(() => {
    if(ref.current) {
      observerRef.current.observe(ref.current);
    }

    return () => {
      observerRef.current.disconnect();
    };
  }, [ref]);

  return isOnScreen;
}

